import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, X, Loader2 } from "lucide-react";
import { API } from "../App";
import { hoardify } from "./Hoard";

const GROUPS = [["pages", "Pages"], ["events", "Events"], ["orgs", "Orgs · StarFlower"], ["businesses", "Businesses"], ["gems", "HOARD Gems"]];

// Magnifying glass beside the logo → site-wide search (pages, events, orgs, businesses, Gems).
const SiteSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); return undefined; }
    setBusy(true);
    const t = setTimeout(() => {
      axios.get(`${API}/search`, { params: { q: term } }).then((r) => setRes(r.data)).catch(() => setRes(null)).finally(() => setBusy(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);
  const total = res ? GROUPS.reduce((n, [k]) => n + (res[k]?.length || 0), 0) : 0;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Search THE WALL" className="p-2 rounded-full text-[#2E5C55] hover:bg-[#F0EDE6] transition-colors" data-testid="site-search-button">
        <Search className="h-5 w-5" />
      </button>
      {open && createPortal( // on <body>: the navbar's blur would otherwise trap this full-screen layer inside the bar
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-20 px-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }} data-testid="site-search-overlay">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-[#E2E0DC] overflow-hidden" role="dialog" aria-label="Search">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E0DC]">
              {busy ? <Loader2 className="h-5 w-5 animate-spin text-[#2E5C55]" /> : <Search className="h-5 w-5 text-[#2E5C55]" />}
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages, events, orgs, artists, HOARD gems…" className="flex-1 outline-none text-base text-[#1A1A1A] placeholder:text-[#9a9a9a]" data-testid="site-search-input" />
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="p-1 rounded-full hover:bg-[#F0EDE6]" data-testid="site-search-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="site-search-results">
              {q.trim().length < 2 && <p className="px-3 py-6 text-sm text-[#5C5C5C]">Type at least two letters — try “Peace Day”, “Traditions”, “Julia” or “15% off”.</p>}
              {res && total === 0 && <p className="px-3 py-6 text-sm text-[#5C5C5C]" data-testid="site-search-empty">Nothing on THE WALL matches “{q}” yet.</p>}
              {res && GROUPS.map(([k, label]) => res[k]?.length ? (
                <div key={k} className="mb-2" data-testid={`site-search-group-${k}`}>
                  <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{label}</p>
                  {res[k].map((r) => (
                    <Link key={`${k}-${r.url}`} to={r.url} onClick={() => setOpen(false)} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#FFF7E6]" data-testid="site-search-result">
                      {r.image ? <img src={r.image} alt="" className="h-9 w-9 rounded-md object-cover bg-white border border-[#E2E0DC] shrink-0" /> : <span className="h-9 w-9 rounded-md bg-[#F0EDE6] shrink-0" />}
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-[#1A1A1A] truncate">{hoardify(r.title)}</span>
                        {r.subtitle && <span className="block text-xs text-[#5C5C5C] truncate">{hoardify(r.subtitle)}</span>}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : null)}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default SiteSearch;
